import React, { memo, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import CompletedActivityCard from "../common/CompletedActivityCard";
import CompletedActivityCardSkeleton from "../common/CompletedActivityCardSkeleton";
import { fetchRegionalQuestionnaireHistory } from "../../utils/backend/pdxBackendClient";
import { QUESTIONNAIRES } from "../../utils/constants";
import { buildQuestionnaireReviewPath } from "../../utils/questionnaireQuestions/reviewNavigation";
import { formatDateParts } from "../../utils/results/recordingIdTime";
import {
  getQuestionnaireMaxScore,
  hasReportableScore,
  shouldShowQuestionnaireScoreToPatient,
} from "../../utils/questionnaireQuestions/questionnaireScores";

const QUESTIONNAIRE_META = Object.fromEntries(
  QUESTIONNAIRES.map(({ slug, labelKey }) => [slug, { labelKey }]),
);

function normalizeQuestionnaireHistoryEntry(
  entry,
  locale,
  questionnaireAssignments,
) {
  const questionnaireId =
    typeof entry?.questionnaireId === "string"
      ? entry.questionnaireId.trim().toLowerCase()
      : "";

  if (!questionnaireId) {
    return null;
  }

  const timestamp =
    entry?.takenAt || entry?.submittedAt || entry?.clientSubmittedAt || null;
  const date =
    typeof timestamp === "string" && timestamp.trim()
      ? new Date(timestamp)
      : null;
  const { time, date: formattedDate } = formatDateParts(date, locale);
  const meta = QUESTIONNAIRE_META[questionnaireId];

  return {
    id:
      typeof entry?.docId === "string" && entry.docId.trim()
        ? entry.docId
        : `${questionnaireId}:${timestamp || "unknown"}`,
    questionnaireId,
    labelKey: meta?.labelKey ?? null,
    fallbackLabel: questionnaireId,
    time,
    date: formattedDate,
    score: hasReportableScore(
      questionnaireId,
      entry?.score,
      shouldShowQuestionnaireScoreToPatient(
        questionnaireId,
        questionnaireAssignments,
      ),
    )
      ? entry.score
      : null,
    maxScore: getQuestionnaireMaxScore(questionnaireId),
    to:
      typeof entry?.docId === "string" && entry.docId.trim()
        ? buildQuestionnaireReviewPath(questionnaireId, entry.docId)
        : null,
  };
}

function normalizeQuestionnaireHistory(
  entries,
  locale,
  questionnaireAssignments,
) {
  if (!Array.isArray(entries)) {
    return [];
  }

  return entries
    .map((entry) =>
      normalizeQuestionnaireHistoryEntry(
        entry,
        locale,
        questionnaireAssignments,
      ),
    )
    .filter(Boolean);
}

function QuestionnaireHistoryRow({ entry, t }) {
  const label = entry.labelKey
    ? t(entry.labelKey, entry.fallbackLabel)
    : entry.fallbackLabel;
  const meta = [entry.time, entry.date].filter(Boolean).join(" ");
  const showScore = entry.score !== null && entry.maxScore !== null;

  return (
    <CompletedActivityCard
      label={label}
      meta={meta}
      icon="assignment_turned_in"
      to={entry.to}
      trailing={
        showScore ? (
          <span
            className="tabular-nums text-sm font-bold text-typography"
            aria-label={t("questionnaireHistory.scoreAriaLabel", {
              score: entry.score,
              max: entry.maxScore,
              defaultValue: "Score {{score}} out of {{max}}",
            })}
          >
            {entry.score}
            <span className="muted-text font-medium"> / {entry.maxScore}</span>
          </span>
        ) : null
      }
    />
  );
}

export const QuestionnaireHistoryList = memo(function QuestionnaireHistoryList({
  uid,
  refreshKey = 0,
  questionnaireAssignments = [],
}) {
  const { t, i18n } = useTranslation("common");
  const locale = i18n.resolvedLanguage || i18n.language || "en-GB";
  const requestKey = useMemo(
    () =>
      JSON.stringify({
        uid: uid || null,
        refreshKey,
        locale,
      }),
    [locale, refreshKey, uid],
  );
  const [state, setState] = useState({
    loading: false,
    error: null,
    entries: [],
  });

  useEffect(() => {
    if (!uid) {
      setState({
        loading: false,
        error: null,
        entries: [],
      });
      return;
    }

    let cancelled = false;
    setState((previousState) => ({
      ...previousState,
      loading: true,
      error: null,
    }));

    fetchRegionalQuestionnaireHistory()
      .then((response) => {
        if (cancelled) {
          return;
        }

        setState({
          loading: false,
          error: null,
          entries: normalizeQuestionnaireHistory(
            response?.questionnaires,
            locale,
            questionnaireAssignments,
          ),
        });
      })
      .catch((error) => {
        if (cancelled) {
          return;
        }

        setState({
          loading: false,
          error,
          entries: [],
        });
      });

    return () => {
      cancelled = true;
    };
  }, [locale, questionnaireAssignments, requestKey, uid]);

  return (
    <section
      className="w-full min-h-fit h-full flex flex-col gap-2 bg-greyscale-1 p-4 rounded-t-3xl"
      aria-label={t(
        "questionnaireHistory.completedAriaLabel",
        "Completed questionnaires",
      )}
    >
      {state.loading ? (
        <div className="w-full flex flex-col gap-3">
          {Array.from({ length: 3 }, (_, index) => (
            <CompletedActivityCardSkeleton key={index} />
          ))}
        </div>
      ) : state.error ? (
        <p className="muted-text text-center">
          {t(
            "questionnaireHistory.unavailable",
            "We couldn't load your completed questionnaires right now.",
          )}
        </p>
      ) : state.entries.length === 0 ? (
        <p className="muted-text text-center">
          {t("questionnaireHistory.empty", "No completed questionnaires yet.")}
        </p>
      ) : (
        <div className="w-full flex flex-col gap-3">
          {state.entries.map((entry) => (
            <QuestionnaireHistoryRow key={entry.id} entry={entry} t={t} />
          ))}
        </div>
      )}
    </section>
  );
});

export default QuestionnaireHistoryList;
